import { useCallback, useEffect, useMemo, useState } from "react";
import { Participant, Raffle, RaffleContext, StakerInfo } from ".";
import constants from "../constants";
import jungleIdl from "../constants/idl/jungle.json";
import {
  IDL as JundleIdl,
  Jungle as JungleProgram,
} from "../constants/types/jungle";
import rafflesIdl from "../constants/idl/raffles.json";
import {
  IDL as RafflesIdl,
  Raffles as RafflesProgram,
} from "../constants/types/raffles";
import { useWallet } from "@solana/wallet-adapter-react";
import { AnchorProvider, BN, Program } from "@project-serum/anchor";
import {
  Keypair,
  PublicKey,
  Signer,
  SystemProgram,
  SYSVAR_CLOCK_PUBKEY,
  TransactionInstruction,
} from "@solana/web3.js";
import { toast } from "react-toastify";
import useApi from "../hooks/useApi";

const decodeRaffleType = (raffleType: number) => {
  switch (raffleType) {
    case 1:
      return "NFT";
    case 2:
      return "Whitelist Spots";
    case 3:
      return "Merch";
    default:
      throw new Error("Invalid raffle type");
  }
};

const getCustomErrorCode = (err: unknown) => {
  //@ts-ignore
  if (err.logs) {
    //@ts-ignore
    for (let log of err.logs) {
      try {
        return log.match(/0x177\d/i)[0];
      } catch (e) {
        continue;
      }
    }
  }
  return false;
};

const customErrorMessage = (err: unknown) => {
  let code = getCustomErrorCode(err);
  switch (code) {
    case "0x1770":
      return "Total winners limit exceeded!";
    case "0x1771":
      return "You cannot buy more tickets, limit reached!";
    case "0x1772":
      return "Raffle is not ended yet!!";
    default:
      return "Unexpected Error!";
  }
};

export default function RafflesProvider({ connection, children }) {
  const wallet = useWallet();

  const api = useApi();

  // confirmed provider
  const provider = useMemo(() => {
    if (!connection && !wallet) return;
    return new AnchorProvider(connection, wallet as any, {
      preflightCommitment: "confirmed",
    });
  }, [connection, wallet]);

  const [jungleProgram, setJungleProgram] = useState<Program<JungleProgram>>();
  const [stakerInfo, setStakerInfo] = useState<StakerInfo>({
    address: undefined,
    holdings: 0,
    tokens: 0,
  });
  const [rafflesProgram, setRafflesProgram] =
    useState<Program<RafflesProgram>>();
  const [raffles, setRaffles] = useState<Raffle[]>([]);
  const [myRaffles, setMyRaffles] = useState<
    { raffle: Raffle; isWon: boolean }[]
  >([]);

  // Set Programs
  useEffect(() => {
    if (!provider) return;
    setJungleProgram(
      new Program<JungleProgram>(
        JundleIdl,
        jungleIdl.metadata.address,
        provider
      )
    );

    setRafflesProgram(
      new Program<RafflesProgram>(
        RafflesIdl,
        rafflesIdl.metadata.address,
        provider
      )
    );
  }, [provider]);

  const fetchStakerInfo = useCallback(async () => {
    if (!jungleProgram || !wallet.publicKey || !provider) return;
    try {
      const jungleKey = new PublicKey(constants.jungleKey);
      const [jungleAddress] = await PublicKey.findProgramAddress(
        [Buffer.from("jungle", "utf8"), jungleKey.toBuffer()],
        jungleProgram.programId
      );

      const [stakerInfo] = await PublicKey.findProgramAddress(
        [
          Buffer.from("staker", "utf8"),
          jungleAddress.toBuffer(),
          wallet.publicKey.toBuffer(),
        ],
        jungleProgram.programId
      );

      const stakerInfoAccount = await jungleProgram.account.stakerInfo.fetch(
        stakerInfo
      );
      console.log(stakerInfoAccount);
      const si = {
        address: stakerInfo,
        holdings: stakerInfoAccount.holdings.toNumber(),
        tokens: stakerInfoAccount.tokens.toNumber(),
      };
      console.log(si);
      setStakerInfo(si);
      return si;
    } catch (e) {
      console.error("StakerInfo account not found!");
    }
  }, [jungleProgram, wallet.publicKey, provider]);
  useEffect(() => {
    fetchStakerInfo();
  }, [fetchStakerInfo]);

  const fetchData = useCallback(async () => {
    if (!rafflesProgram) return;

    const [project] = await PublicKey.findProgramAddress(
      [Buffer.from("project"), constants.rafflesKey.toBuffer()],
      rafflesProgram.programId
    );

    const rafflesAccounts = await rafflesProgram.account.raffle.all([
      {
        memcmp: {
          offset: 8, // Default Offst
          bytes: project.toString(),
        },
      },
    ]);

    const raffles = (
      await Promise.all(
        rafflesAccounts.map(async (raffle): Promise<Raffle | undefined> => {
          const raffleEnd = new Date(raffle.account.end.toNumber());
          const isClosed = raffleEnd < new Date();
          if (isClosed && raffle.account.tickets.toNumber() === 0) return;

          const participants = await rafflesProgram.account.participant.all([
            {
              memcmp: {
                offset: 8, // Default Offst
                bytes: raffle.account.key.toString(),
              },
            },
          ]);
          const details = (
            await api.get("rafflesDetails/" + raffle.account.key)
          ).data;
          const start = details?.start ? new Date(details.start) : new Date();

          return {
            key: raffle.account.key,
            name: raffle.account.name,
            image: raffle.account.image,
            raffleType: decodeRaffleType(raffle.account.raffleType),
            mint: raffle.account.mint,
            collectionSize: raffle.account.collectionSize.toNumber(),
            price: raffle.account.price.toNumber() / 1000,
            winners: raffle.account.winners.toNumber(),
            start,
            end: raffleEnd,
            maxTickets: raffle.account.maxTickets.toNumber(),
            maxTicketsPerPerson:
              raffle.account.maxTicketsPerParticipant.toNumber() || 999999,
            tickets: raffle.account.tickets.toNumber(),
            participants: participants.map((participant) => ({
              owner: participant.account.owner,
              tickets: participant.account.tickets.toNumber(),
              isWon: participant.account.isWon,
              isDistributed: participant.account.isDistributed,
            })),
            isStarted: start < new Date(),
            isClosed: raffleEnd < new Date(),
          };
        })
      )
    ).filter((r) => r);

    console.log("raffles", raffles);

    //@ts-ignore
    setRaffles(raffles.filter((raffle) => raffle));
  }, [rafflesProgram]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchRaffle = useCallback(
    async (key: PublicKey): Promise<Raffle | undefined> => {
      if (!rafflesProgram) return;

      const [raffleAddress] = await PublicKey.findProgramAddress(
        [Buffer.from("raffle"), key.toBuffer()],
        rafflesProgram.programId
      );

      const raffle = await rafflesProgram.account.raffle.fetch(raffleAddress);

      const participants = await rafflesProgram.account.participant.all([
        {
          memcmp: {
            offset: 8, // Default Offst
            bytes: raffle.key.toString(),
          },
        },
      ]);

      const raffleEnd = new Date(raffle.end.toNumber());

      const details = (await api.get("rafflesDetails/" + raffle.key)).data;
      const start = details?.start ? new Date(details.start) : new Date();

      return {
        key: raffle.key,
        name: raffle.name,
        image: raffle.image,
        raffleType: decodeRaffleType(raffle.raffleType),
        mint: raffle.mint,
        collectionSize: raffle.collectionSize.toNumber(),
        price: raffle.price.toNumber() / 1000,
        winners: raffle.winners.toNumber(),
        end: raffleEnd,
        start,
        maxTickets: raffle.maxTickets.toNumber(),
        maxTicketsPerPerson:
          raffle.maxTicketsPerParticipant.toNumber() || 999999,
        tickets: raffle.tickets.toNumber(),
        participants: participants.map((participant) => ({
          owner: participant.account.owner,
          tickets: participant.account.tickets.toNumber(),
          isWon: participant.account.isWon,
          isDistributed: participant.account.isDistributed,
        })),
        isStarted: start < new Date(),
        isClosed: raffleEnd < new Date(),
      };
    },
    [rafflesProgram]
  );

  const refreshRaffle = useCallback(
    async (key: PublicKey): Promise<Raffle | undefined> => {
      const raffle = await fetchRaffle(key);
      if (!raffle) return;
      const index = raffles.findIndex((raffle) => raffle.key.equals(key));
      if (index !== -1) {
        const newRaffles = [...raffles];
        newRaffles[index] = raffle;
        setRaffles(newRaffles);
      }
      return raffle;
    },
    [raffles]
  );

  const fetchMyRaffles = useCallback(async () => {
    if (!rafflesProgram || !wallet.publicKey) return [];

    const myParticipations = await rafflesProgram.account.participant.all([
      {
        memcmp: {
          offset: 8 + 32 + 1, // Default Offst
          bytes: wallet.publicKey.toString(),
        },
      },
    ]);
    const [project] = await PublicKey.findProgramAddress(
      [Buffer.from("project"), constants.rafflesKey.toBuffer()],
      rafflesProgram.programId
    );

    const raffles = await Promise.all(
      myParticipations.map(
        async (
          participation
        ): Promise<{ raffle: Raffle; isWon: boolean } | undefined> => {
          const raffleKey = participation.account.raffle;
          const [raffleAddress] = await PublicKey.findProgramAddress(
            [Buffer.from("raffle"), raffleKey.toBuffer()],
            rafflesProgram.programId
          );

          try {
            const raffle = await rafflesProgram.account.raffle.fetch(
              raffleAddress
            );
            if (raffle.project.toString() !== project.toString()) return;
            const participants = await rafflesProgram.account.participant.all([
              {
                memcmp: {
                  offset: 8, // Default Offst
                  bytes: raffle.key.toString(),
                },
              },
            ]);

            const details = (await api.get("rafflesDetails/" + raffle.key))
              .data;
            const start = details?.start ? new Date(details.start) : new Date();

            return {
              raffle: {
                key: raffle.key,
                name: raffle.name,
                image: raffle.image,
                raffleType: decodeRaffleType(raffle.raffleType),
                mint: raffle.mint,
                collectionSize: raffle.collectionSize.toNumber(),
                price: raffle.price.toNumber() / 1000,
                winners: raffle.winners.toNumber(),
                end: new Date(raffle.end.toNumber()),
                start,
                maxTickets: raffle.maxTickets.toNumber(),
                maxTicketsPerPerson:
                  raffle.maxTicketsPerParticipant.toNumber() || 999999,
                tickets: raffle.tickets.toNumber(),
                participants: participants.map((participant) => ({
                  owner: participant.account.owner,
                  tickets: participant.account.tickets.toNumber(),
                  isWon: participant.account.isWon,
                  isDistributed: participant.account.isDistributed,
                })),
                isStarted: start < new Date(),
                isClosed: new Date(raffle.end.toNumber()) < new Date(),
              },
              isWon: participation.account.isWon,
            };
          } catch (error) {
            return;
          }
        }
      )
    );

    //@ts-ignore
    setMyRaffles(raffles.filter((raffle) => raffle));
  }, [rafflesProgram, wallet.publicKey]);
  useEffect(() => {
    fetchMyRaffles();
  }, [fetchMyRaffles]);

  const fetchMyEntry = useCallback(
    async (key: PublicKey): Promise<Participant | undefined> => {
      if (!rafflesProgram || !wallet.publicKey) return;

      const raffle = await fetchRaffle(key);
      if (!raffle) return;

      return raffle.participants.find(
        (part) => part.owner.toString() == wallet.publicKey?.toString()
      );
    },
    [rafflesProgram, wallet.publicKey]
  );

  const getTransactionBatch = useCallback(async (): Promise<
    [TransactionInstruction[], Signer[], PublicKey] | undefined
  > => {
    if (!jungleProgram || !wallet.publicKey) return;

    const [jungleAddress] = await PublicKey.findProgramAddress(
      [Buffer.from("jungle", "utf8"), constants.jungleKey.toBuffer()],
      jungleProgram.programId
    );

    const [stakerInfoAddress] = await PublicKey.findProgramAddress(
      [
        Buffer.from("staker", "utf8"),
        jungleAddress.toBuffer(),
        wallet.publicKey.toBuffer(),
      ],
      jungleProgram.programId
    );

    let stakerInfo;

    try {
      stakerInfo = await jungleProgram.account.stakerInfo.fetch(
        stakerInfoAddress
      );
    } catch (e) {
      throw Error("Staker Info not found");
    }

    const latestBatch = stakerInfo.transactionBatches.at(-1);
    if (latestBatch) {
      //@ts-ignore
      const latestBatchAccount: TransactionBatch =
        await jungleProgram.account.transactionBatch.fetch(latestBatch);
      if (latestBatchAccount.transactions.length < 200) {
        // @ts-ignore
        return [[], [], latestBatch];
      }
    }

    const transactionBatch = Keypair.generate();
    const instruction = jungleProgram.instruction.initTransactionBatch({
      accounts: {
        transactionBatch: transactionBatch.publicKey,
        stakerInfo: stakerInfoAddress,
        staker: wallet.publicKey,
        systemProgram: SystemProgram.programId,
      },
    });

    return [[instruction], [transactionBatch], transactionBatch.publicKey];
  }, [jungleProgram, wallet, provider]);

  const buyTickets = useCallback(
    async (rafflekey: PublicKey, tickets: number) => {
      if (!rafflesProgram || !jungleProgram || !wallet.publicKey) return;

      if (!stakerInfo.address) {
        toast.error("You need to stake for $GOD.");
        return;
      }

      const buyTicketsToast = toast.loading(`Buying ${tickets} tickets`);

      const raffleAccount = await fetchRaffle(rafflekey);
      if (!raffleAccount) return;
      console.log("Test", raffleAccount?.price * tickets);
      if (raffleAccount?.price * tickets > stakerInfo.tokens / 1000) {
        return toast.update(buyTicketsToast, {
          render: `You do not have enough $GOD.`,
          type: "error",
          isLoading: false,
          closeOnClick: true,
          closeButton: true,
          autoClose: 4000,
        });
      }

      const [jungle] = await PublicKey.findProgramAddress(
        [Buffer.from("jungle"), constants.jungleKey.toBuffer()],
        jungleProgram.programId
      );

      const transactionBatch = await getTransactionBatch();
      if (!transactionBatch) throw Error("Transaction Batch not found");
      const [tbIx, tbSign, tbPub] = transactionBatch;

      const [raffle] = await PublicKey.findProgramAddress(
        [Buffer.from("raffle", "utf8"), rafflekey.toBuffer()],
        rafflesProgram.programId
      );

      const [participant, participantBump] = await PublicKey.findProgramAddress(
        [
          Buffer.from("participant", "utf8"),
          rafflekey.toBuffer(),
          wallet.publicKey.toBuffer(),
        ],
        rafflesProgram.programId
      );

      const instructions: TransactionInstruction[] = [];

      try {
        // To see if the participant is already initialized
        await rafflesProgram.account.participant.fetch(participant);
      } catch (e) {
        instructions.push(
          rafflesProgram.instruction.initParticipant(participantBump, {
            accounts: {
              raffle,
              participant,
              owner: wallet.publicKey,
              systemProgram: SystemProgram.programId,
            },
          })
        );
      }

      try {
        await rafflesProgram.rpc.buyTickets(new BN(tickets), {
          accounts: {
            jungle,
            stakerInfo: stakerInfo.address,
            transactionBatch: tbPub,
            raffle,
            participant,
            owner: wallet.publicKey,
            jungleProgram: jungleProgram.programId,
            clock: SYSVAR_CLOCK_PUBKEY,
          },
          instructions: [...tbIx, ...instructions],
          signers: [...tbSign],
        });

        toast.update(buyTicketsToast, {
          render: `Bought ${tickets} tickets`,
          type: "success",
          isLoading: false,
          closeOnClick: true,
          closeButton: true,
          autoClose: 4000,
        });

        fetchMyRaffles();
        fetchStakerInfo();
      } catch (e) {
        toast.update(buyTicketsToast, {
          render: customErrorMessage(e),
          type: "error",
          isLoading: false,
          closeOnClick: true,
          closeButton: true,
          autoClose: 4000,
        });
        console.error(e);
      }
    },
    [rafflesProgram, jungleProgram, wallet.publicKey, stakerInfo]
  );

  return (
    <RaffleContext.Provider
      value={{
        raffles,
        myRaffles,
        stakerInfo,
        fetchRaffle,
        refreshRaffle,
        fetchMyEntry,
        buyTickets,
      }}
    >
      {children}
    </RaffleContext.Provider>
  );
}
